import React, { useRef } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE FILTER-PANEL LAYOUT — MASTER–DETAIL. Category rail on the left, that
 * category's controls on the right. Nothing is hidden behind a second click:
 * one group is always open.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🔻 **THE GAG BREAKDOWN ADAPTER IS DELETED (2026-10-02, owner ruling) — the
 * Breakdown's `Filters ▾` is gone at every width — so the order pages
 * (`OrderFilterBar`) are this component's only caller. Every Breakdown
 * reference below is archaeology, kept for the reasoning it carries.**
 *
 * 🔀 **EXTRACTED FROM `components/gags/GagFiltersMasterDetail.js` ON
 * 2026-08-10, AND IT RENDERS BYTE-IDENTICAL DOM.** That file was then a thin
 * adapter that supplies the Gag Breakdown's six categories, its two counters and
 * its group bodies; every class string, every `data-testid`, every `id`, the
 * roving `tabIndex`, the key handling and the `Clear filters` row came across
 * unchanged. The move was proven, not asserted: the twelve rendered DOM states
 * (six categories × two filter states) were snapshotted before the extraction
 * and compared after.
 *
 * **Why extract at all** — the order pages needed the same layout, and the
 * original could not give it to them: it imported `GAG_FILTER_GROUPS`,
 * `filterGroupCount` and `activeFilterCount` as MODULE IMPORTS and hard-rendered
 * `GagFilterGroupBody`, so the layout and one surface's filter vocabulary were
 * welded together. This is the same move the project has already made three
 * times — `GagDropdown` → `ui/Dropdown`, `GagSearchInput` → `ui/SearchInput`,
 * `GagFilterChips` → `ui/FilterChips` — and it is the Code Unification
 * Principle rather than a refactor for its own sake.
 *
 * 🚨 **THIS FILE KNOWS NOTHING ABOUT ANY SURFACE'S FILTER MODEL, AND MUST NOT
 * LEARN.** It never reads a `filters` object; it takes `groupCount(key)` and
 * `canClear` already computed. That is what lets two surfaces whose models are
 * MIRROR IMAGES share it — see the COUNT DIRECTION box below, which is the trap
 * this component is most likely to be broken by.
 *
 * ♿ **IT IS A TABLIST, AND THAT IS THE POINT RATHER THAN A STYLE CHOICE.** A
 * left rail whose selection drives a right pane IS the tab pattern, so it takes
 * the pattern's semantics wholesale instead of a hand-rolled approximation:
 * `role="tablist"` + `role="tab"` + `aria-selected`, roving `tabIndex` (exactly
 * one rail button is in the tab order, so Tab moves THROUGH the rail into the
 * controls rather than around every category), arrow keys with wraparound, and
 * `Home`/`End`. The pane is a `role="tabpanel"` associated with its tab both
 * ways (`id`/`aria-controls`, `aria-labelledby`).
 *   - **Both axes of arrow key are handled** (Up/Down AND Left/Right). The rail
 *     is `aria-orientation="vertical"` because that is what it is from `sm:` up,
 *     but it wraps to a HORIZONTAL chip row below `sm` — and `aria-orientation`
 *     cannot carry a breakpoint. Accepting both keys is what keeps the control
 *     honest on a phone, where the vertical-only contract would be a lie.
 *   - **Activation follows focus.** Switching panes is free (no fetch, no mount
 *     cost beyond a few controls), so the rail behaves like a rail: one arrow
 *     press, one visible change.
 *   - The pane carries **no `tabIndex`**: every group's body contains focusable
 *     controls, so making the panel itself a tab stop would add a stop that
 *     lands on nothing.
 *   - The rail buttons are real `<button>`s, so the global focus ring from
 *     `index.css:12-18` (dark twin `:38-42`) applies. No `focus:outline-none`
 *     appears in this file, which is what leaves that ring intact.
 *
 * 🔑 **THE PER-CATEGORY INDICATOR IS WHAT MAKES A RAIL HONEST, NOT DECORATION.**
 * Only one category's controls are on screen at a time, so without it a user
 * would have to visit every one to find out what is narrowing the list. If a
 * future group cannot produce a count, it does not belong on this rail.
 *
 * 🚨 **IT IS TEXT — "3 selected" — AND DELIBERATELY NOT A BADGE.** The trigger
 * already carries a `(N)` badge counting FIELDS that differ from the surface's
 * defaults, treating a multi-select as ONE field however many options are
 * picked. This counts SELECTED OPTIONS. So with three statuses on, the trigger
 * reads (1) and this tab reads "3 selected": both correct, and the whole reason
 * the indicator names its own unit rather than being a second pill with a bare
 * number in it. Two badges of the same shape invite arithmetic that does not
 * work; "(1) filters" beside "3 selected" does not.
 *   - A FRACTION (`3/6`) was rejected because the denominator is data-dependent
 *     on the Breakdown: Sub-department's is the number of sub-departments in the
 *     current department and Owner's is the size of its roster, so the same tab
 *     reads a different fraction in each department and none of them means
 *     anything.
 *   - A BARE DOT was rejected: it discards the one number worth having, and it
 *     would make colour the sole carrier of "something is filtering here" —
 *     which drags in WCAG 1.4.11's 3:1 in both themes plus an `sr-only`/`title`
 *     channel, i.e. more work than the text costs. As text it is governed by
 *     1.4.3 and carries its meaning in a non-colour channel by construction.
 *
 * ♿ The indicator is rendered ALWAYS and is the button's own accessible-name
 * suffix; it is merely `sr-only` when the count is 0, so the rail stays quiet
 * visually while a screen reader still hears "Sub-department, none selected".
 * There is no `aria-hidden` badge with a parallel `sr-only` copy to keep in step.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🪤 COUNT DIRECTION IS THE CALLER'S PROBLEM, AND IT IS THE TRAP HERE
 * ═══════════════════════════════════════════════════════════════════════════
 * `groupCount` is a PROP, and `countLabel` is a prop beside it, because the two
 * surfaces that use this component have **mirror-image filter models** and a
 * shared counter would be wrong on one of them:
 *   - The **Gag Breakdown** defaults ALL-OFF (`DEFAULT_FILTERS.statuses` is
 *     `[]`, meaning "show everything"), so a SELECTION is what narrows. Its
 *     count is the number selected and reads *"3 selected"*.
 *   - The **order pages** default ALL-ON (every status shown), so an EXCLUSION
 *     is what narrows. Their count must be the number HIDDEN and must read
 *     *"3 hidden"*.
 * Wiring the order pages to the Breakdown's `filterGroupCount` would make a
 * resting order panel announce **"7 selected"** on a filter that is filtering
 * nothing — the exact defect `constants/gagBreakdown.js` already records for the
 * old owner-scope string. `CLAUDE.md` states the binding cross-surface rule that
 * this follows from: **`✕` must always RELAX a filter, never tighten it**, which
 * is why the order pages chip their EXCLUSIONS. **Work out which way round a
 * surface is FROM ITS DEFAULT before writing either function**, and never
 * "unify" the two — they disagree on purpose.
 *
 * 🚨 **AND THE DIRECTION CAN DIFFER BETWEEN GROUPS ON ONE SURFACE, WHICH IS WHY
 * `countLabel` TAKES THE GROUP KEY.** Department Orders is the live example and
 * it is not an edge case: its **Status** group defaults all-on so its count is
 * EXCLUSIONS and reads *"2 hidden"*, while its **Production** group defaults to
 * "all productions" so a chosen production is a SELECTION and reads
 * *"1 selected"*. A per-surface label alone would have been wrong on one of the
 * two groups of the very first surface that needed it.
 *
 * 🎨 `text-gray-600 dark:text-gray-500` is a MEASURED pair, not a guess, and the
 * usual `text-gray-500 dark:text-gray-400` would have been wrong here.
 * `client/src/index.css:244-270` force-overrides every gray text class in dark
 * mode with `!important`, so when two of them sit on one element the one LATER
 * IN THAT FILE wins — the `dark:` variant does not necessarily deliver its own
 * colour. Resolved: light `#4b5563` on white = **7.13:1**; dark `#9ca3af` on the
 * panel's `#1e293b` (itself an `index.css` override of `dark:bg-gray-800`) =
 * **6.26:1**. Both clear 1.4.3's 4.5:1. The tempting
 * `text-gray-500 dark:text-gray-400` resolves to `#6b7280` in dark = **3.16:1**
 * and FAILS — which is why this file does not copy the section-label pair.
 *
 * 📱 Below `sm` the rail becomes a wrapping row above the pane rather than a
 * narrow column beside it — `ui/Dropdown` clamps the panel to
 * `max-w-[calc(100vw-1rem)]`, and a 9rem rail out of ~344px would leave two date
 * inputs too narrow to render a date.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 "CLEAR FILTERS" IS THE RAIL'S LAST ROW — AND IT IS AN ACTION, NOT A
 *    CATEGORY, WHICH IS THE WHOLE OF THE IMPLEMENTATION
 * ═══════════════════════════════════════════════════════════════════════════
 * Product owner, 2026-08-09: *"filtes seem to work well but in the master list
 * lets have a clear filters at the bottom as the last option"*.
 *
 * 🚨 **IT SITS OUTSIDE THE TABLIST, IN A WRAPPER THAT OWNS THE RAIL'S VISUAL
 * BOX.** The rail is a real tablist with a roving `tabIndex` and arrow-key
 * navigation, so a `<button>` dropped INSIDE it would do two wrong things at
 * once and neither is visible on screen: it would become an arrow-key stop that
 * selects no category (the key handler is on the tablist element, so a keydown
 * from a child bubbles into it), and it would sit in a group whose semantics are
 * `role="tab"` + `aria-selected` — semantics an action cannot honour. So the
 * structure is:
 *
 *     <div rail shell>          ← border/width/stacking; NOT a tablist
 *       <div role="tablist">    ← the categories, roving tabIndex
 *       <hr>
 *       <button>Clear filters   ← ordinary tab stop, no role, no aria-selected
 *     </div>
 *
 * The button is a plain sibling, so it is reachable by ordinary Tab (default tab
 * order, no `tabIndex` attribute of its own) and completely invisible to the
 * rail's `onKeyDown`. Pinned BOTH ways by §10 of
 * `GagFiltersPopover.masterDetail.test.js` — structurally (it is not a
 * descendant of the tablist) and behaviourally (an ArrowDown dispatched ON the
 * button changes no category and moves no focus), because the structural
 * assertion alone would survive someone re-attaching the handler higher up.
 *
 * 🔑 **IT IS `aria-disabled`, NOT `disabled`, AND THAT IS DELIBERATE.** A native
 * `disabled` button leaves the tab order entirely, so the control would
 * disappear for a keyboard user in exactly the state they are most likely to be
 * exploring it in — and Chromium does not fire pointer events on a disabled
 * control, so its `title` never appears either. `aria-disabled` keeps it
 * focusable and hoverable while the click handler refuses.
 *
 * 🔴 **IT IS DISABLED, NEVER HIDDEN.** Hiding it would change the rail's height
 * as the user filters — the one part of this panel whose position should be
 * learnable — and would make the control vanish at the moment someone goes
 * looking for it.
 *
 * 🔑 **`canClear` COMES IN ALREADY COMPUTED, AND THE CALLER MUST DERIVE IT FROM
 * THE SAME NUMBER AS ITS TRIGGER BADGE.** On the Breakdown that is
 * `activeFilterCount(filters) > 0`. Passing something else — most temptingly a
 * sum of `groupCount` — makes the button live when the badge is blank, because a
 * per-group option count reads 0 for a category-less filter that is still
 * filtering.
 *
 * 🔑 **IT CLEARS FILTERS ONLY — THE SELECTED CATEGORY IS UNTOUCHED.** The
 * highlighted rail tab is navigation state, not filter state; resetting it would
 * swap the pane the user is looking at out from under them for no reason. The
 * popover also stays OPEN, like every other control in these panels, which apply
 * live.
 *
 * 🔤 **THE LABEL IS "Clear filters" AND THE CHIP ROW'S IS "Clear all". THEY
 * DIFFER ON PURPOSE AND MUST RUN THE SAME FUNCTION.** Inside a panel showing ONE
 * category, "Clear all" is ambiguous ("all of this category?"), whereas at the
 * end of a row of chips "all" plainly means all the chips. The PAGE passes one
 * callback to both, so the two labels can never come to mean two things.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔀 SINGLE-GROUP MODE — ONE CATEGORY RENDERS NO RAIL AT ALL
 * ═══════════════════════════════════════════════════════════════════════════
 * With `groups.length <= 1` this renders the group's body alone: no rail, no
 * tablist, no `Clear filters` row. **That is not a new idea, it is the existing
 * one applied inside the component.** `GagFiltersPopover.js` already special-
 * cases Labour view with the reasoning written out — *"a category rail over a
 * single control is nonsense. Do not 'complete the feature' by giving Labour a
 * layout — and note that means NO `Clear filters` row here either: it is the
 * rail's last row, and there is no rail."* Pinned by §9 of that file's tests.
 *
 * It exists because a surface can legitimately have a category count that varies
 * at runtime: a group may be admin-only, or depend on data that is absent. A
 * one-tab tablist is a worse control than the plain panel it replaced — it costs
 * a click, a focus stop and a rail's width to offer no choice.
 *
 * ⚠️ **NO GAG CALLER CAN REACH THIS BRANCH** (`GAG_FILTER_GROUPS` is a module
 * constant of six), which is why the extraction stayed byte-identical. Do not
 * "simplify" it away on the grounds that nothing seems to use it.
 *
 * Props:
 *  - groups: `[{ key, label }]` — may be built conditionally by the caller.
 *  - active: the selected group key; onSelect(key). **Held by the PARENT** so it
 *    survives the popover closing and reopening — `ui/Dropdown` unmounts its
 *    panel when closed.
 *  - renderGroup(key) → node: the detail pane's contents. A SLOT. This component
 *    must never inline a control of its own.
 *  - groupCount(key) → number: selected/excluded options in that group. See the
 *    COUNT DIRECTION box — the direction is the caller's to get right.
 *  - countLabel(n, key) → string: how that number reads, per group. Defaults to
 *    the Breakdown's `" 3 selected"` / `" none selected"`.
 *  - canClear, onClearFilters(), clearLabel, clearTitle, clearDisabledTitle.
 *    Omit `onClearFilters` to render no clear row at all.
 *  - idPrefix: namespaces every `id` and `data-testid` (`gag-filter` on the
 *    Breakdown). 🚨 A TEST CONTRACT on every surface — do not rename one.
 *  - railLabel: the tablist's accessible name.
 */

/**
 * 🪤 THE LEADING SPACE IS LOAD-BEARING. Without it the tab announces
 * "Statusnone selected" — the label and the count are two inline spans, so the
 * accessible-name algorithm inserts no separator of its own. Invisible on
 * screen, because the count sits on its own line.
 */
export const DEFAULT_COUNT_LABEL = (n) => (n > 0 ? ` ${n} selected` : ' none selected');

/**
 * The order pages' Status reading. Their status set defaults ALL-ON, so the
 * number that means "this group is narrowing the list" is the count of EXCLUDED
 * statuses — the same `hiddenCount` already behind the `Filters ▾ (N)` badge.
 * Exported here beside its opposite so the two readings sit together and nobody
 * has to go looking for the second one to notice the asymmetry exists.
 */
export const HIDDEN_COUNT_LABEL = (n) => (n > 0 ? ` ${n} hidden` : ' none hidden');

export default function FiltersMasterDetail({
  groups,
  active,
  onSelect,
  renderGroup,
  groupCount,
  countLabel = DEFAULT_COUNT_LABEL,
  canClear = false,
  onClearFilters,
  clearLabel = 'Clear filters',
  clearTitle = 'Reset every filter. The search box is not affected.',
  clearDisabledTitle = 'No filters to clear',
  idPrefix,
  railLabel = 'Filter categories',
}) {
  const tabRefs = useRef({});

  const activeIndex = Math.max(0, groups.findIndex((g) => g.key === active));
  const activeGroup = groups[activeIndex];

  const move = (nextIndex) => {
    const next = groups[nextIndex];
    if (!next) return;
    onSelect(next.key);
    tabRefs.current[next.key]?.focus();
  };

  const onKeyDown = (e) => {
    const last = groups.length - 1;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        move(activeIndex === last ? 0 : activeIndex + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        move(activeIndex === 0 ? last : activeIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(last);
        break;
      default:
        break;
    }
  };

  /* 🔀 SINGLE-GROUP MODE — see the block above. No rail, so no tablist and no
     `Clear filters`; and no `role="tabpanel"` either, because a tabpanel with no
     tab to point back at is a broken ARIA relationship rather than a spare one.
     The pane keeps its `data-testid` so a caller's tests can reach the body by
     the same name in both modes. */
  if (!activeGroup) return null;
  if (groups.length <= 1) {
    return (
      <div data-testid={`${idPrefix}-pane`} className="min-w-0">
        {renderGroup(activeGroup.key)}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* THE RAIL SHELL — it owns the rail's visual box (the divider, the fixed
          width from `sm:` up) so that the tablist inside it can hold NOTHING
          but tabs. The `Clear filters` row is its second and last child; see
          the block at the top of this file for why it cannot live inside the
          tablist. Below `sm` the shell is a column of [wrapping chip row,
          divider, action] rather than a single wrapping row, so the action is
          still the last ROW and never wraps in among the categories. */}
      <div
        data-testid={`${idPrefix}-rail-shell`}
        className="flex flex-col gap-1 pb-2 border-b border-gray-200 dark:border-gray-700 sm:w-[9rem] sm:shrink-0 sm:pb-0 sm:pr-2 sm:border-b-0 sm:border-r"
      >
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-label={railLabel}
          onKeyDown={onKeyDown}
          data-testid={`${idPrefix}-rail`}
          className="flex flex-wrap gap-1 sm:flex-col sm:flex-nowrap"
        >
          {groups.map((g) => {
            const n = groupCount(g.key);
            const isActive = g.key === activeGroup.key;
            return (
              <button
                key={g.key}
                type="button"
                role="tab"
                id={`${idPrefix}-tab-${g.key}`}
                aria-selected={isActive}
                aria-controls={`${idPrefix}-pane-${g.key}`}
                tabIndex={isActive ? 0 : -1}
                ref={(el) => { tabRefs.current[g.key] = el; }}
                onClick={() => onSelect(g.key)}
                data-testid={`${idPrefix}-tab-${g.key}`}
                className={`flex flex-col items-start px-2 py-1.5 rounded-lg border text-left text-sm transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 font-semibold'
                    : 'bg-transparent text-gray-800 dark:text-gray-200 border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <span className="max-w-full truncate">{g.label}</span>
                <span
                  className={
                    n > 0
                      ? 'text-[11px] font-normal text-gray-600 dark:text-gray-500'
                      : 'sr-only'
                  }
                  data-testid={`${idPrefix}-tab-count-${g.key}`}
                >
                  {/* 🪤 The leading space lives in `countLabel` — see
                      `DEFAULT_COUNT_LABEL`. A surface supplying its own must
                      keep it. The GROUP KEY is passed because one surface's
                      groups can count in opposite directions; see the COUNT
                      DIRECTION box. */}
                  {countLabel(n, g.key)}
                </span>
              </button>
            );
          })}
        </div>

        {onClearFilters && (
          <>
            {/* The same hairline the Breakdown's Owner group uses between its
                scopes and its people list — a separator, not a border on the
                button, so the button keeps its own `rounded-lg` hover shape. */}
            <div className="my-0.5 border-t border-gray-200 dark:border-gray-700" />

            {/* 🚨 NO `role`, NO `aria-selected`, NO `tabIndex` — it is an ACTION
                sitting under the tablist, not an extra category. `border
                border-transparent` matches the tabs' border box so the label
                lines up with them to the pixel rather than sitting 1px in.
                🎨 The enabled pair is the inactive tab's own
                `text-gray-800 dark:text-gray-200`; the disabled pair is
                `GagFilterGroupBody`'s `ROW_DISABLED_CLS` pair, so a panel has ONE
                look for "unavailable". Both measured in the browser in both
                themes. */}
            <button
              type="button"
              data-testid={`${idPrefix}-clear`}
              aria-disabled={canClear ? undefined : true}
              title={canClear ? clearTitle : clearDisabledTitle}
              onClick={() => { if (canClear) onClearFilters(); }}
              className={`w-full px-2 py-1.5 rounded-lg border border-transparent text-left text-sm transition-colors ${
                canClear
                  ? 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }`}
            >
              {clearLabel}
            </button>
          </>
        )}
      </div>

      <div
        role="tabpanel"
        id={`${idPrefix}-pane-${activeGroup.key}`}
        aria-labelledby={`${idPrefix}-tab-${activeGroup.key}`}
        data-testid={`${idPrefix}-pane`}
        className="flex-1 min-w-0"
      >
        {renderGroup(activeGroup.key)}
      </div>
    </div>
  );
}
